import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import DatePicker from "../components/DatePicker";
import ElementCombobox from "../components/ElementCombobox";
import { elementLabel } from "../data/elements";
import { printDecayReport } from "./decay/decayReport";
import { ArrowLeft, Atom, Calculator, Clock, FlaskConical, Printer, AlertCircle, Zap } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Escape sequences are interpreted in JavaScript strings but taken literally
// inside JSX text, which is how this symbol once shipped as eight characters in
// the isotope dropdown. Built here, where it is a string.
const HALF_LIFE_SYMBOL = "t\u00bd";

const UNITS = ["Bq", "kBq", "MBq", "GBq", "Ci", "mCi", "\u00b5Ci"];

const TO_BQ = {
  Bq: 1,
  kBq: 1e3,
  MBq: 1e6,
  GBq: 1e9,
  Ci: 3.7e10,
  mCi: 3.7e7,
  "\u00b5Ci": 3.7e4,
};

const TO_SECONDS = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  years: 365.25 * 86400,
};

const TIME_UNITS = ["seconds", "minutes", "hours", "days", "years"];

// Numbers and dates follow the language chosen in Core rather than the one the
// operating system happens to be set to.
function localeFor(language) {
  return language === "it" ? "it-IT" : "en-US";
}

/** An activity, written the same way whatever its size.
 *
 *  This used to localise whole numbers and not fractional ones: an integer went
 *  through toLocaleString and a decimal through toFixed. On an Italian page
 *  1234 became "1.234" while 1234.5 stayed "1234.5000", so the same figure was
 *  punctuated two different ways depending only on whether it was round, and
 *  "1.234" could be read as either one thousand or as one and a bit.
 */
function formatNumber(num, language) {
  if (num === 0) return "0";
  if (Math.abs(num) < 0.001 || Math.abs(num) >= 1e9) {
    return num.toExponential(4);
  }
  return num.toLocaleString(localeFor(language), {
    maximumFractionDigits: 4,
    minimumFractionDigits: 0,
  });
}

/** Half-lives span microseconds to 10^17 years, so a fixed number of decimal
 *  places is wrong at one end or the other. Short values keep their precision;
 *  very small ones go to exponent rather than a row of zeroes. */
function formatHalfLife(value) {
  const n = Number(value);
  if (!isFinite(n)) return String(value);
  if (n !== 0 && (Math.abs(n) < 0.01 || Math.abs(n) >= 1e7)) {
    return n.toExponential(3).replace("e+", "e");
  }
  return String(n);
}

function formatElapsed(seconds) {
  const totalMin = seconds / 60;
  if (totalMin < 1) return `${seconds.toFixed(1)} s`;
  if (totalMin < 60) return `${totalMin.toFixed(2)} min`;
  const h = Math.floor(totalMin / 60);
  const m = Math.round(totalMin % 60);
  if (h < 24) return `${h} h ${m} min (${totalMin.toFixed(2)} min)`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return `${d} d ${rh} h ${m} min`;
}

export default function DecayCalculator() {
  const { t, language } = useLanguage();
  const s = localStrings(language);
  const [tab, setTab] = useState("datetime");
  const [elements, setElements] = useState([]);
  const [selectedElementId, setSelectedElementId] = useState("");
  const [selectedIsotopeId, setSelectedIsotopeId] = useState("");
  const [activity, setActivity] = useState("");
  const [inputUnit, setInputUnit] = useState("MBq");
  const [outputUnit, setOutputUnit] = useState("MBq");
  const [originDate, setOriginDate] = useState("");
  const [originTime, setOriginTime] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [targetTime, setTargetTime] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Quick tab state
  const [qActivity, setQActivity] = useState("");
  const [qInputUnit, setQInputUnit] = useState("MBq");
  const [qOutputUnit, setQOutputUnit] = useState("MBq");
  const [qTimeValue, setQTimeValue] = useState("");
  const [qTimeUnit, setQTimeUnit] = useState("hours");
  const [qDirection, setQDirection] = useState("after");

  const fetchElements = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/elements`, { withCredentials: true });
      setElements(data);
    } catch {
      toast.error(t("toast.failedLoad"));
    }
  }, [t]);

  useEffect(() => { fetchElements(); }, [fetchElements]);

  const selectedElement = useMemo(
    () => elements.find((e) => e.id === selectedElementId),
    [elements, selectedElementId]
  );

  const isotopes = selectedElement?.isotopes || [];

  const selectedIsotope = useMemo(
    () => isotopes.find((i) => i.id === selectedIsotopeId),
    [isotopes, selectedIsotopeId]
  );

  // The element is stored as a symbol; the readable name comes from the
  // language, not from the database.
  const elementName = selectedElement ? elementLabel(selectedElement.name, language) : "";

  const halfLifeText = (iso) =>
    `${formatHalfLife(iso.half_life_value)} ${t("decay.timeUnit_" + iso.half_life_unit)}`;

  const handleElementChange = (val) => {
    setSelectedElementId(val);
    setSelectedIsotopeId("");
    setResult(null);
    setError(null);
  };

  const canCalculate =
    selectedIsotope &&
    activity &&
    parseFloat(activity) > 0 &&
    originDate && originTime &&
    targetDate && targetTime;

  const handleCalculate = () => {
    if (!canCalculate) return;

    const [oY, oM, oD] = originDate.split("-").map(Number);
    const [oH, oMin] = originTime.split(":").map(Number);
    const [tY, tM, tD] = targetDate.split("-").map(Number);
    const [tH, tMin] = targetTime.split(":").map(Number);

    const originDt = new Date(oY, oM - 1, oD, oH, oMin, 0, 0);
    const targetDt = new Date(tY, tM - 1, tD, tH, tMin, 0, 0);
    const elapsed_s = (targetDt.getTime() - originDt.getTime()) / 1000;

    if (elapsed_s <= 0) {
      setResult(null);
      setError(t("decay.errorTargetBeforeOrigin"));
      return;
    }

    setError(null);
    const A0_bq = parseFloat(activity) * TO_BQ[inputUnit];
    const halfLife_s = selectedIsotope.half_life_value * TO_SECONDS[selectedIsotope.half_life_unit];

    const decayFactor = Math.pow(0.5, elapsed_s / halfLife_s);
    const A_bq = A0_bq * decayFactor;
    const A_out = A_bq / TO_BQ[outputUnit];

    setResult({
      element: elementName,
      isotope: selectedIsotope.name,
      halfLife: halfLifeText(selectedIsotope),
      initialActivity: `${formatNumber(parseFloat(activity), language)} ${inputUnit}`,
      originDt: originDt.toLocaleString(localeFor(language)),
      targetDt: targetDt.toLocaleString(localeFor(language)),
      elapsed: formatElapsed(elapsed_s),
      decayFactor: decayFactor.toExponential(6),
      finalActivity: formatNumber(A_out, language),
      finalUnit: outputUnit,
    });
  };

  // Quick Calculator, live result
  const quickResult = useMemo(() => {
    if (!selectedIsotope || !(parseFloat(qActivity) > 0) || !(parseFloat(qTimeValue) > 0)) return null;

    const A0_bq = parseFloat(qActivity) * TO_BQ[qInputUnit];
    const halfLife_s = selectedIsotope.half_life_value * TO_SECONDS[selectedIsotope.half_life_unit];
    const time_s = parseFloat(qTimeValue) * TO_SECONDS[qTimeUnit];
    const elapsed_s = qDirection === "after" ? time_s : -time_s;

    const decayFactor = Math.pow(0.5, elapsed_s / halfLife_s);
    const A_bq = A0_bq * decayFactor;
    const A_out = A_bq / TO_BQ[qOutputUnit];

    return {
      element: elementName,
      isotope: selectedIsotope.name,
      halfLife: halfLifeText(selectedIsotope),
      initialActivity: `${formatNumber(parseFloat(qActivity), language)} ${qInputUnit}`,
      timeDelta: `${formatNumber(parseFloat(qTimeValue), language)} ${t("decay.timeUnit_" + qTimeUnit)} ${qDirection === "before" ? t("decay.quickBefore").toLowerCase() : t("decay.quickAfter").toLowerCase()}`,
      decayFactor: decayFactor.toExponential(6),
      finalActivity: formatNumber(A_out, language),
      finalUnit: qOutputUnit,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIsotope, elementName, qActivity, qInputUnit, qOutputUnit, qTimeValue, qTimeUnit, qDirection, t, language]);

  // The sheet itself lives in ./decay/decayReport. A blocked pop-up is the one
  // outcome the page has to account for; everything else it handles.
  const handlePrintResult = (res) => {
    const opened = printDecayReport({
      result: res,
      t,
      locale: localeFor(language),
    });
    if (!opened) toast.error(t("common.actionFailed"));
  };

  // Element picker and isotope dropdown, identical on both tabs.
  const pickers = (idPrefix) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>{t("decay.element")}</Label>
        <ElementCombobox
          elements={elements}
          value={selectedElementId}
          onChange={handleElementChange}
          testId={`${idPrefix}-element-select`}
        />
      </div>
      <div className="space-y-2">
        <Label>{t("decay.isotope")}</Label>
        <Select
          value={selectedIsotopeId}
          onValueChange={(v) => { setSelectedIsotopeId(v); setResult(null); }}
          disabled={!selectedElementId}
        >
          <SelectTrigger data-testid={`${idPrefix}-isotope-select`} className="border-slate-200">
            <SelectValue placeholder={selectedElementId ? t("decay.selectIsotope") : t("decay.selectElementFirst")} />
          </SelectTrigger>
          <SelectContent>
            {isotopes.map((iso) => (
              <SelectItem key={iso.id} value={iso.id}>
                {`${iso.name} (${HALF_LIFE_SYMBOL} = ${halfLifeText(iso)})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Link to="/" data-testid="back-to-home" className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("decay.backToHome")}
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
              <Atom className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("decay.title")}</h1>
          </div>

          {/* Tab Selector */}
          <div className="flex bg-slate-100 p-1 rounded-lg mb-6" data-testid="decay-tabs">
            <button
              onClick={() => setTab("datetime")}
              data-testid="decay-tab-datetime"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${tab === "datetime" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            >
              <Clock className="w-4 h-4 shrink-0" />
              {t("decay.tabDateTime")}
            </button>
            <button
              onClick={() => setTab("quick")}
              data-testid="decay-tab-quick"
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${tab === "quick" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            >
              <Zap className="w-4 h-4 shrink-0" />
              {t("decay.tabQuick")}
            </button>
          </div>

          <AnimatePresence mode="wait">
            {/* ==================== DATE & TIME TAB ==================== */}
            {tab === "datetime" && (
              <motion.div key="datetime" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} transition={{ duration: 0.15 }}>
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
                  {pickers("decay")}

                  {/* Row 2: Activity + Input Unit */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2 space-y-2">
                      <Label>{t("decay.initialActivity")}</Label>
                      <Input
                        data-testid="decay-activity-input"
                        type="number"
                        min="0"
                        step="any"
                        value={activity}
                        onChange={(e) => { setActivity(e.target.value); setResult(null); }}
                        placeholder="0.00"
                        className="border-slate-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("decay.inputUnit")}</Label>
                      <Select value={inputUnit} onValueChange={(v) => { setInputUnit(v); setResult(null); }}>
                        <SelectTrigger data-testid="decay-input-unit" className="border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 3: Origin date/time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 shrink-0" />{t("decay.originDate")}</Label>
                      <DatePicker
                        testId="decay-origin-date"
                        value={originDate}
                        onChange={(v) => { setOriginDate(v); setResult(null); }}
                        placeholder={s("date.choose")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("decay.originTime")}</Label>
                      <Input
                        data-testid="decay-origin-time"
                        type="time"
                        value={originTime}
                        onChange={(e) => { setOriginTime(e.target.value); setResult(null); }}
                        className="border-slate-200"
                      />
                    </div>
                  </div>

                  {/* Row 4: Target date/time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 shrink-0" />{t("decay.targetDate")}</Label>
                      <DatePicker
                        testId="decay-target-date"
                        value={targetDate}
                        onChange={(v) => { setTargetDate(v); setResult(null); setError(null); }}
                        placeholder={s("date.choose")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("decay.targetTime")}</Label>
                      <Input
                        data-testid="decay-target-time"
                        type="time"
                        value={targetTime}
                        onChange={(e) => { setTargetTime(e.target.value); setResult(null); setError(null); }}
                        className="border-slate-200"
                      />
                    </div>
                  </div>

                  {/* Row 5: Output Unit + Calculate */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                    <div className="space-y-2">
                      <Label>{t("decay.outputUnit")}</Label>
                      <Select value={outputUnit} onValueChange={(v) => { setOutputUnit(v); setResult(null); }}>
                        <SelectTrigger data-testid="decay-output-unit" className="border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Button
                        onClick={handleCalculate}
                        disabled={!canCalculate}
                        data-testid="decay-calculate-btn"
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-10"
                      >
                        <Calculator className="w-4 h-4 mr-2" />
                        {t("decay.calculate")}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Error Display */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="decay-error"
                    className="mt-6 bg-red-50 border border-red-200 rounded-xl p-5 flex items-center gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                    <p className="text-red-700 font-medium">{error}</p>
                  </motion.div>
                )}

                {/* Result Card */}
                {result && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="decay-result-card"
                    className="mt-6 bg-white border border-emerald-200 rounded-xl p-6 shadow-sm"
                  >
                    {/* The print label is a sentence in Italian, so it needs room
                        to drop below the heading on a narrow screen. */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div className="flex items-center gap-2 min-w-0">
                        <FlaskConical className="w-5 h-5 text-emerald-600 shrink-0" />
                        <h2 className="text-lg font-semibold text-slate-900">{t("decay.result")}</h2>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePrintResult(result)}
                        data-testid="decay-print-btn"
                        className="border-slate-200 text-slate-600 hover:text-slate-800 shrink-0"
                      >
                        <Printer className="w-4 h-4 mr-1.5" />
                        {t("decay.print")}
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.element")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.element}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.isotope")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.isotope}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.halfLife")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.halfLife}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.initialActivity")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.initialActivity}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.originDateTime")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.originDt}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.targetDateTime")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.targetDt}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.elapsedTime")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.elapsed}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-slate-500">{t("decay.decayFactor")}</span>
                        <p className="font-medium text-slate-800 break-words">{result.decayFactor}</p>
                      </div>
                    </div>

                    <div className="mt-5 pt-5 border-t border-emerald-100">
                      <span className="text-sm text-slate-500">{t("decay.finalActivity")}</span>
                      <p className="text-3xl font-bold text-emerald-700 mt-1 break-words" data-testid="decay-final-activity">
                        {result.finalActivity} <span className="text-lg font-medium text-emerald-500">{result.finalUnit}</span>
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* ==================== QUICK TAB ==================== */}
            {tab === "quick" && (
              <motion.div key="quick" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.15 }}>
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">
                  {pickers("quick")}

                  {/* Row 2: Activity + Input Unit */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2 space-y-2">
                      <Label>{t("decay.initialActivity")}</Label>
                      <Input
                        data-testid="quick-activity-input"
                        type="number"
                        min="0"
                        step="any"
                        value={qActivity}
                        onChange={(e) => setQActivity(e.target.value)}
                        placeholder="0.00"
                        className="border-slate-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("decay.inputUnit")}</Label>
                      <Select value={qInputUnit} onValueChange={setQInputUnit}>
                        <SelectTrigger data-testid="quick-input-unit" className="border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 3: Output Unit */}
                  <div className="space-y-2">
                    <Label>{t("decay.outputUnit")}</Label>
                    <Select value={qOutputUnit} onValueChange={setQOutputUnit}>
                      <SelectTrigger data-testid="quick-output-unit" className="border-slate-200 max-w-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Row 4: Time value + Time unit + Before/After toggle */}
                  <div className="space-y-2">
                    <Label>{t("decay.quickTimeLabel")}</Label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        data-testid="quick-time-value"
                        type="number"
                        min="0"
                        step="any"
                        value={qTimeValue}
                        onChange={(e) => setQTimeValue(e.target.value)}
                        placeholder="0"
                        className="border-slate-200 flex-1"
                      />
                      <Select value={qTimeUnit} onValueChange={setQTimeUnit}>
                        <SelectTrigger data-testid="quick-time-unit" className="border-slate-200 w-full sm:w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_UNITS.map((u) => <SelectItem key={u} value={u}>{t("decay.timeUnit_" + u)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0" data-testid="quick-direction-toggle">
                        <button
                          onClick={() => setQDirection("before")}
                          data-testid="quick-direction-before"
                          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${qDirection === "before" ? "bg-emerald-600 text-white" : "bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
                        >
                          {t("decay.quickBefore")}
                        </button>
                        <button
                          onClick={() => setQDirection("after")}
                          data-testid="quick-direction-after"
                          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${qDirection === "after" ? "bg-emerald-600 text-white" : "bg-white text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
                        >
                          {t("decay.quickAfter")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Result Card, live */}
                <AnimatePresence>
                  {quickResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      data-testid="quick-result-card"
                      className="mt-6 bg-white border border-emerald-200 rounded-xl p-6 shadow-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <FlaskConical className="w-5 h-5 text-emerald-600 shrink-0" />
                          <h2 className="text-lg font-semibold text-slate-900">{t("decay.result")}</h2>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintResult(quickResult)}
                          data-testid="quick-print-btn"
                          className="border-slate-200 text-slate-600 hover:text-slate-800 shrink-0"
                        >
                          <Printer className="w-4 h-4 mr-1.5" />
                          {t("decay.print")}
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                        <div className="min-w-0">
                          <span className="text-slate-500">{t("decay.element")}</span>
                          <p className="font-medium text-slate-800 break-words">{quickResult.element}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-slate-500">{t("decay.isotope")}</span>
                          <p className="font-medium text-slate-800 break-words">{quickResult.isotope}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-slate-500">{t("decay.halfLife")}</span>
                          <p className="font-medium text-slate-800 break-words">{quickResult.halfLife}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-slate-500">{t("decay.initialActivity")}</span>
                          <p className="font-medium text-slate-800 break-words">{quickResult.initialActivity}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-slate-500">{t("decay.quickTimeDelta")}</span>
                          <p className="font-medium text-slate-800 break-words">{quickResult.timeDelta}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-slate-500">{t("decay.decayFactor")}</span>
                          <p className="font-medium text-slate-800 break-words">{quickResult.decayFactor}</p>
                        </div>
                      </div>

                      <div className="mt-5 pt-5 border-t border-emerald-100">
                        <span className="text-sm text-slate-500">{t("decay.finalActivity")}</span>
                        <p className="text-3xl font-bold text-emerald-700 mt-1 break-words" data-testid="quick-final-activity">
                          {quickResult.finalActivity} <span className="text-lg font-medium text-emerald-500">{quickResult.finalUnit}</span>
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Empty state if no elements */}
          {elements.length === 0 && (
            <div className="mt-6 text-center py-12 bg-white border border-slate-200 rounded-xl">
              <Atom className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t("decay.noElements")}</p>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
